// packages/web/src/components/Chat/ChatList.tsx - Mobile Responsive Version

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Badge,
  Divider
} from '@mui/material';
import { Chat } from '@matchpoint/shared';
import { format, isToday, isYesterday } from 'date-fns';

interface ChatListProps {
  chats: Chat[];
  selectedChatId?: string;
  onChatSelect: (chat: Chat) => void;
  currentUserId: string;
}

export const ChatList: React.FC<ChatListProps> = ({
  chats,
  selectedChatId,
  onChatSelect,
  currentUserId
}) => {
  const getOtherParticipantName = (chat: Chat): string => {
    const currentUserIndex = chat.participants.indexOf(currentUserId);
    if (currentUserIndex === -1) return 'Unknown';
    
    const otherUserIndex = currentUserIndex === 0 ? 1 : 0;
    return chat.participantNames[otherUserIndex];
  };

  const formatChatDate = (date: Date): string => {
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  const isChatActive = (chat: Chat): boolean => {
    return new Date() <= chat.expiresAt;
  };

  if (chats.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%',
        p: { xs: 2, sm: 3 }
      }}>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          No messages yet. When you accept a challenge, a chat will be created here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        width: { xs: '4px', sm: '6px' }
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderRadius: '3px'
      }
    }}>
      <List sx={{ p: 0 }}>
        {chats.map((chat, index) => {
          const otherParticipantName = getOtherParticipantName(chat);
          const isSelected = chat.id === selectedChatId;
          const isActive = isChatActive(chat);

          return (
            <React.Fragment key={chat.id}>
              <ListItem
                disablePadding
                sx={{
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': {
                    bgcolor: isSelected ? 'action.selected' : 'action.hover'
                  }
                }}
              >
                <ListItemButton 
                  onClick={() => onChatSelect(chat)}
                  sx={{ 
                    py: { xs: 1, sm: 1.5 },
                    px: { xs: 1.5, sm: 2 }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: { xs: 40, sm: 56 } }}>
                    <Badge
                      color="success"
                      variant="dot"
                      invisible={!isActive}
                      sx={{
                        '& .MuiBadge-dot': {
                          width: { xs: 6, sm: 8 },
                          height: { xs: 6, sm: 8 }
                        }
                      }}
                    >
                      <Avatar sx={{ 
                        bgcolor: 'primary.main',
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>
                        {otherParticipantName.charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: isActive ? 600 : 400,
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {otherParticipantName}
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography 
                          component="span"
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: 'block',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }}
                        >
                          {chat.courtName}
                        </Typography>
                        <Typography 
                          component="span"
                          variant="caption" 
                          color={isActive ? 'primary' : 'text.disabled'}
                          sx={{ 
                            display: 'block',
                            fontSize: { xs: '0.65rem', sm: '0.7rem' }
                          }}
                        >
                          {formatChatDate(chat.matchTime)}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItemButton>
              </ListItem>
              {index < chats.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};